/**
 * Shared labor cost calculation (Scheduling, Plan vs Actual shift cost on mobile, etc.).
 * BaseRate meaning follows PaymentType + PaymentFrequency (aligned with PayrollGrossRules.TryDeriveHourlyBase).
 */
import { PayRecord, Shift } from '../types';
import { deriveShiftDurationHours } from './shiftHours';

/** Same factor as PayrollGrossRules.AverageWeeksPerMonth */
export const AVERAGE_WEEKS_PER_MONTH = 4.33;

function frequencyIsMonthly(name: string): boolean {
  return name.includes('Month');
}

function frequencyIsBiweekly(name: string): boolean {
  const n = name.toLowerCase();
  return (n.includes('bi') && n.includes('week')) || n.includes('fortnight');
}

function isSalaryLikePaymentType(paymentTypeName: string): boolean {
  const t = paymentTypeName.toLowerCase();
  return t.includes('salary') || t.includes('management');
}

/** Salary / management: BaseRate period gross → hourly for “Allocated Hours”. */
function salaryLikeBaseToHourlyAllocated(
  baseRate: number,
  contractedHours: number,
  frequencyName: string
): number {
  if (contractedHours <= 0 || baseRate <= 0) return 0;
  const freq = frequencyName || '';
  if (frequencyIsMonthly(freq)) {
    return baseRate / (contractedHours * AVERAGE_WEEKS_PER_MONTH);
  }
  if (frequencyIsBiweekly(freq)) {
    return baseRate / 2 / contractedHours;
  }
  return baseRate / contractedHours;
}

/** Salary / management: BaseRate + preset days → hourly. */
function salaryLikeBaseToHourlyPresetDays(
  baseRate: number,
  contractedHours: number,
  presetDaysCount: number,
  frequencyName: string
): number {
  if (contractedHours <= 0 || presetDaysCount <= 0 || baseRate <= 0) return 0;
  const ratio = presetDaysCount / 5;
  const freq = frequencyName || '';
  if (frequencyIsMonthly(freq)) {
    const monthlyHours = contractedHours * AVERAGE_WEEKS_PER_MONTH * ratio;
    return monthlyHours > 0 ? baseRate / monthlyHours : 0;
  }
  if (frequencyIsBiweekly(freq)) {
    const weeklyGross = baseRate / 2;
    const weeklyHours = contractedHours * ratio;
    return weeklyHours > 0 ? weeklyGross / weeklyHours : 0;
  }
  const adjustedHours = contractedHours * ratio;
  return adjustedHours > 0 ? baseRate / adjustedHours : 0;
}

/**
 * Hourly € for labor cost: uses PaymentType, PaymentFrequency, AssignLaborCostBy, BaseRate / DailyRate.
 */
export function calculateHourlyRateFromPayRecord(payRecord: PayRecord | undefined): number {
  if (!payRecord) return 0;

  const paymentTypeName = payRecord.paymentType?.name || '';
  const baseRate = payRecord.baseRate || 0;
  const frequencyName = payRecord.paymentFrequency?.name || '';

  if (paymentTypeName === 'Hourly') {
    return baseRate;
  }

  if (paymentTypeName.includes('Project') && baseRate > 0) {
    return baseRate;
  }

  if (paymentTypeName === 'Daily' && payRecord.dailyRate != null && payRecord.dailyRate > 0) {
    const wd = payRecord.workingDaysPerWeek || 5;
    const ch = payRecord.contractedHours || 40;
    const hpd = ch / wd;
    return hpd > 0 ? payRecord.dailyRate / hpd : 0;
  }

  const assignLaborCostByName = payRecord.assignLaborCostBy?.name || '';
  const contractedHours = payRecord.contractedHours || 0;
  const workingDaysPerWeek = payRecord.workingDaysPerWeek || 0;
  const dailyRate = payRecord.dailyRate || 0;
  const presetDaysCount = payRecord.payRecordWorkingDays?.length || 0;

  if (!assignLaborCostByName) return 0;

  const salaryLike = isSalaryLikePaymentType(paymentTypeName);

  if (assignLaborCostByName === 'Allocated Hours' && contractedHours > 0) {
    if (baseRate <= 0) return 0;
    if (salaryLike) {
      return salaryLikeBaseToHourlyAllocated(baseRate, contractedHours, frequencyName);
    }
    if (frequencyIsMonthly(frequencyName)) {
      const monthlyHours = contractedHours * AVERAGE_WEEKS_PER_MONTH;
      return monthlyHours > 0 ? baseRate / monthlyHours : 0;
    }
    if (frequencyIsBiweekly(frequencyName)) {
      return baseRate / 2 / contractedHours;
    }
    return baseRate / contractedHours;
  }

  if (assignLaborCostByName === 'Daily Rate' && workingDaysPerWeek > 0) {
    if (dailyRate > 0 && contractedHours > 0) {
      const weeklyTotal = dailyRate * workingDaysPerWeek;
      return weeklyTotal / contractedHours;
    }
    return 0;
  }

  if (assignLaborCostByName === 'Pre Set Days' && presetDaysCount > 0) {
    if (contractedHours <= 0 || baseRate <= 0) return 0;
    if (salaryLike) {
      return salaryLikeBaseToHourlyPresetDays(baseRate, contractedHours, presetDaysCount, frequencyName);
    }
    if (frequencyIsMonthly(frequencyName)) {
      const monthlyHours = contractedHours * AVERAGE_WEEKS_PER_MONTH * (presetDaysCount / 5);
      return monthlyHours > 0 ? baseRate / monthlyHours : 0;
    }
    if (frequencyIsBiweekly(frequencyName)) {
      const weeklyGross = baseRate / 2;
      const weeklyHours = contractedHours * (presetDaysCount / 5);
      return weeklyHours > 0 ? weeklyGross / weeklyHours : 0;
    }
    const adjustedHours = contractedHours * (presetDaysCount / 5);
    return adjustedHours > 0 ? baseRate / adjustedHours : 0;
  }

  if (assignLaborCostByName === 'Do not Allocate') return 0;

  return 0;
}

export function getShiftPaidHoursForCost(shift: Shift): number {
  const breakTimePaid = shift.breakTimePaid ?? false;
  const paid = shift.paidHours != null ? Number(shift.paidHours) : null;
  const unpaid = Number(shift.unpaidHours ?? 0);
  const hasStoredHours = paid != null && (paid > 0 || unpaid > 0);

  if (hasStoredHours) {
    if (!breakTimePaid) return paid!;
    return paid! + unpaid;
  }

  const total = deriveShiftDurationHours(shift);
  if (breakTimePaid) return total;
  return Math.max(0, total - unpaid);
}

export function getShiftLaborCost(
  shift: Shift,
  employeePayRecords: Record<number, PayRecord>
): number {
  const payRecord = employeePayRecords[shift.employeeId];
  if (!payRecord) return 0;
  const hourlyRate = calculateHourlyRateFromPayRecord(payRecord);
  const shiftHours = getShiftPaidHoursForCost(shift);
  return Math.round(hourlyRate * shiftHours * 100) / 100;
}

function payRecordEffectiveYmd(pr: PayRecord): string {
  const raw = pr.effectiveFrom ?? (pr.isActive ? pr.fromDate : undefined) ?? pr.fromDate;
  return raw ? String(raw).slice(0, 10) : '';
}

export function buildActivePayRecordMap(payRecords: PayRecord[]): Record<number, PayRecord> {
  const map: Record<number, PayRecord> = {};
  (payRecords || []).forEach((pr) => {
    if (!pr.isActive || !pr.employeeId) return;
    const existing = map[pr.employeeId];
    const d = payRecordEffectiveYmd(pr);
    const ed = existing ? payRecordEffectiveYmd(existing) : '';
    if (!existing || (d && (!ed || d > ed))) {
      map[pr.employeeId] = pr;
    }
  });
  return map;
}
